import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { createVendorStaffSchema } from "../../../validation/admin/vendoValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";
import { generateUserId } from "../../../utils/helper";
import AsyncSearchSelect from "../../ui/form/AsyncSearchSelect";
import FileInputField from "../../ui/form/FileInputField";
import CheckboxField from "../../ui/form/CheckboxField";


const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });

      if (!values.stateId) {
        setFieldValue("stateId", "");
      }
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });

      if (!values.cityId) {
        setFieldValue("cityId", "");
      }
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

const CorporateWatcher = ({ fetchCorporateVendors, resetVendors }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const corporateId = values.corporateCodeId?.[0];
    if (corporateId) {
      fetchCorporateVendors({ corporateCodeId: corporateId });
      setFieldValue("addedBy", "");
    } else {
      resetVendors();
      setFieldValue("addedBy", "");
    }
  }, [values.corporateCodeId?.[0]]);

  return null;
};





// ===================================================================
// MAIN COMPONENT START
// ===================================================================
const CreateVendorStaff = ({ closeModal, callback }) => {
  const [preview, setPreview] = useState(null);


  const { error, isLoading, post, serverErrors } = usePost(
    apiRequest.createVendor
  );


  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });





  // ===============================================================
  // INITIAL FORM VALUES
  // ===============================================================
  const initialValues = {
    name: "",
    countryId: "6937b28dda25e4d40acc2983",
    stateId: "6937b35cda25e4d40acc4ce4",
    cityId: "",
    mobile: "",
    email: "",
    contactPerson: "",
    displayName: "",

    userId: "",
    password: "",
    vendorType: "vendor",
    isSuperVendor: false,
    userDetails: [{ name: "", mobile: "", email: "" }],
    corporateCodeId: [""],
    addedBy: "",
    image: null,
  };


  const UserIdWatcher = ({ generateUserId }) => {
    const { values, setFieldValue } = useFormikContext();
    useEffect(() => {
      const corpName = values.mobile;
      if (values.name && corpName) {
        setFieldValue("userId", generateUserId(values.name, corpName));
      }
    }, [values.name, values.mobile]);

    return null;
  };


  const { data: corporateCodes } = useFetch({
    apiMethod: apiRequest.getCorporateCodeForSelect,
    body: { paginate: false },
  });

  const {
    data: vendors,
    refetch: fetchCorporateVendors,
    setBlankResponse: resetVendors,
  } = useFetch({
    apiMethod: apiRequest.getCorporateCodeByVendorId,
    autoFetch: false,
  });

  

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Create Vendor Manager Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        validationSchema={createVendorStaffSchema}
        validateOnBlur
        validateOnChange
        onSubmit={async (values, { resetForm }) => {
          console.log(values);

          const res = await post({ data: { body: values } });
          if (res.response) {
            callback?.();
            resetForm();
            closeModal();
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />
            <CorporateWatcher fetchCorporateVendors={fetchCorporateVendors} resetVendors={resetVendors} />

            <UserIdWatcher generateUserId={generateUserId} />

            <form
              onSubmit={handleSubmit}
              className="text-sm max-h-[80vh] overflow-auto"
            >
              {error && <Alert message={error} type="error" />}

              {/* =========================================================== */}
              {/* BASIC INFO */}
              {/* =========================================================== */}
              <div className="grid grid-cols-1 p-4 gap-2 sm:grid-cols-3">
                <InputField
                  name="name"
                  label="Vendor Manager Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                />
                <InputField
                  name="displayName"
                  label="Display Name"
                  value={values.displayName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.displayName && errors.displayName}
                />

                <InputField
                  name="mobile"
                  label="Mobile"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mobile && errors.mobile}
                />

                <InputField
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && errors.email}
                />

                {/* Country */}
                <SelectField
                  name="countryId"
                  label="Country"
                  value={values.countryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                />

                {/* State */}
                <SelectField
                  name="stateId"
                  label="State"
                  value={values.stateId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                />

                {/* City */}
                <SelectField
                  name="cityId"
                  label="City"
                  value={values.cityId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                />

                <InputField
                  type="password"
                  name="password"
                  label="Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                />

                <div className="">
                  <FileInputField
                    error={touched.image && errors.image}
                    id="image"
                     label="Company Logo Upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0] || null;
                      setFieldValue("image", file);
                      setPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      className="object-contain w-20 h-20 mt-2 border rounded"
                    />
                  )}
                  <CheckboxField
                    checked={values.removeBg}
                    name="removeBg"
                    label=" Remove Background"
                    id="removeBg"
                    value={values.removeBg}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    serverError={serverErrors?.removeBg}
                    error={touched.removeBg && errors.removeBg}
                  />
                </div>

                <div className="col-span-full hidden mt-3">
                  <h3 className="font-semibold mb-2">Partner Details</h3>
                  <FieldArray name="userDetails">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Mobile</th>
                              <th className="p-2 border">Email</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.userDetails.map((row, index) => (
                              <tr key={index}>
                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.name`}
                                    placeholder="Enter name"
                                    value={row.name}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.mobile`}
                                    placeholder="Enter mobile"
                                    value={row.mobile}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2">
                                  <InputField
                                    name={`userDetails.${index}.email`}
                                    placeholder="Enter email"

                                    value={row.email}
                                    onChange={handleChange}
                                  />
                                </td>

                                <td className="border p-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({ name: "", mobile: "", email: "" })
                            }
                            className="mt-2 px-3 py-1 text-white bg-blue-600 rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add User
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>
                </div>




              </div>
              <div className="p-2">
                <div className="grid grid-cols-2 gap-2">

                  <SelectField
                    name="corporateCodeId"
                    label="Corporate Code"
                    value={values.corporateCodeId[0] || ""}
                    onChange={(e) => {
                      const value = e?.target?.value;
                      setFieldValue("corporateCodeId", [value]);
                    }}
                    error={
                      touched.corporateCodeId?.[0] &&
                      errors.corporateCodeId?.[0]
                    }
                    options={corporateCodes?.corporatecodes?.map((c) => ({
                      value: c.id,
                      label: `${c.name} (${c.code})`,
                    }))}
                  />




                  {/* Vendor */}
                  <SelectField
                    name="addedBy"
                    label="Vendor"
                    value={values.addedBy}
                    onChange={handleChange}
                    error={touched.addedBy && errors.addedBy}
                    options={vendors?.map((v) => ({
                      value: v._id,
                      label: v.name,
                    }))}
                  />

                </div>
              </div>


              <div className="flex justify-end gap-2 border-t p-4">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>

                <Button type="submit" variant="primary" isLoading={isLoading}>
                  Create Vendor Staff
                </Button>
              </div>

            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateVendorStaff;
